// usePaywallConfig — hook returning the server-driven paywall variant
// (header/features/CTA/footer/badges + display option flags) resolved
// to the current language.
//
// Two-phase like useQuizCopy: localStorage cache for warm start, fresh
// fetch on mount. Falls back to a bundled control-variant blob so the
// paywall never renders empty during an outage or first-cold-launch.

import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { nativePlatform } from "@/lib/native";

const LS_KEY = (lang) => `calofay_paywall_config_v2_${lang}`;

// On Android the paywall must reference Google Play, not Apple.
// The backend config is authored for iOS (Apple review requires it),
// so we swap store-specific copy at render time when the WebView is
// running inside the Android app.
function platformizeConfig(data) {
  if (!data || !data.config) return data;
  if (nativePlatform() !== "android") return data;
  const replaceApple = (s) =>
    typeof s === "string"
      ? s
          .replace(/إعدادات Apple ID/g, "إعدادات Google Play")
          .replace(/إعدادات Apple/g, "إعدادات Google Play")
          .replace(/الدفع عبر Apple/g, "الدفع عبر Google Play")
          .replace(/your Apple ID settings/gi, "your Google Play settings")
          .replace(/your Apple settings/gi, "your Google Play settings")
          .replace(/Billed by Apple/gi, "Billed by Google Play")
          .replace(/\bApple\b/g, "Google Play")
      : s;
  const cfg = data.config;
  return {
    ...data,
    config: {
      ...cfg,
      header: cfg.header
        ? { ...cfg.header, title: replaceApple(cfg.header.title), subtitle: replaceApple(cfg.header.subtitle) }
        : cfg.header,
      footer: cfg.footer ? { ...cfg.footer, text: replaceApple(cfg.footer.text) } : cfg.footer,
    },
  };
}

// Bundled fallback — matches paywall_config_seed.json on the backend so
// stale caches see the same shape as a fresh install.
const BUNDLED_AR = {
  variant: "control",
  config: {
    header: {
      title: "اشترك لتفتح كل الميزات",
      subtitle: "جرب مجاناً 3 أيام. ألغِ في أي وقت من إعدادات Apple.",
    },
    features: [
      { icon: "camera", label: "صور وجباتك — احسب السعرات بلحظة" },
      { icon: "brain", label: "مدرب ذكاء اصطناعي عربي 24/7" },
      { icon: "target", label: "خطة شخصية على حسب هدفك" },
      { icon: "activity", label: "متابعة الوزن والماكروز والخطوات" },
    ],
    cta: {
      // Owner (Feb 2026): dropped the "ابدأ 3 أيام مجانية" trial
      // framing because many Apple regions don't honour the trial on
      // first install — showing it then charging up-front felt like
      // a bait-and-switch. Unified copy is "اشترك الآن" regardless of
      // whether the product carries an introductory offer.
      text_trial: "اشترك الآن",
      text_no_trial: "اشترك الآن",
    },
    footer: {
      text: "الدفع عبر Apple · يتجدد الاشتراك تلقائياً · يمكنك الإلغاء في أي وقت من إعدادات Apple ID",
    },
    badges: {
      best_value: "الأكثر توفيراً",
      save_pct_template: "الأكثر توفيراً · وفّر {pct}%",
    },
    options: {
      show_monthly_equivalent: true,
      show_savings_badge: true,
      show_features_list: true,
      show_footer: true,
    },
  },
};

const BUNDLED_EN = {
  variant: "control",
  config: {
    header: {
      title: "Subscribe to unlock all features",
      subtitle: "Try free for 3 days. Cancel anytime from your Apple settings.",
    },
    features: [
      { icon: "camera", label: "Snap your meals — instant calorie count" },
      { icon: "brain", label: "AI Coach available 24/7" },
      { icon: "target", label: "Personal plan tailored to your goal" },
      { icon: "activity", label: "Track weight, macros, and steps" },
    ],
    cta: {
      // See AR note above — unified copy regardless of trial state.
      text_trial: "Subscribe Now",
      text_no_trial: "Subscribe Now",
    },
    footer: {
      text: "Billed by Apple · Auto-renews · Cancel anytime from your Apple ID settings",
    },
    badges: {
      best_value: "Best value",
      save_pct_template: "Best value · Save {pct}%",
    },
    options: {
      show_monthly_equivalent: true,
      show_savings_badge: true,
      show_features_list: true,
      show_footer: true,
    },
  },
};

export function usePaywallConfig(lang) {
  const [data, setData] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY(lang));
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.config) return parsed;
      }
    } catch (_) { /* ignore */ }
    return lang === "en" ? BUNDLED_EN : BUNDLED_AR;
  });

  const langRef = useRef(lang);
  if (langRef.current !== lang) {
    langRef.current = lang;
    try {
      const raw = localStorage.getItem(LS_KEY(lang));
      setData(raw ? JSON.parse(raw) : (lang === "en" ? BUNDLED_EN : BUNDLED_AR));
    } catch (_) {
      setData(lang === "en" ? BUNDLED_EN : BUNDLED_AR);
    }
  }

  useEffect(() => {
    let alive = true;
    api.get(`/paywall/config?lang=${lang}`)
      .then((r) => {
        if (!alive) return;
        const payload = r?.data;
        if (payload && payload.config) {
          setData(payload);
          try { localStorage.setItem(LS_KEY(lang), JSON.stringify(payload)); } catch (_) {}
        }
      })
      .catch(() => { /* keep whatever we have */ });
    return () => { alive = false; };
  }, [lang]);

  return platformizeConfig(data);
}
